import { CancelTokenSource } from 'axios';
import { DateTime } from 'luxon';

import {
  Manifest,
  ManifestResponse,
  ManifestSearchCriteria,
  ManifestSearchResult,
  ShipmentSearchResult,
} from '@/model';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'shipment/manifest';

export interface ManifestCriteria {
  scac: string;
  originLocationId?: number;
  originLocationCode?: string;
  manifestDate: DateTime;
  ownerNaturalId?: string;
}

export interface ManifestPreviewPage {
  page: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export class ShipmentManifestController extends BaseHttpController {
  constructor() {
    super(endpoint, { canIncludeSubDomains: true });
  }

  async getManifest(
    manifestId: number,
    cancelToken?: CancelTokenSource,
  ): Promise<Manifest> {
    const response = await this.http.get<Manifest>(`${manifestId}`, {
      cancelToken: cancelToken?.token,
    });
    return new Manifest(response.data);
  }

  async searchManifests(
    criteria: ManifestSearchCriteria,
    cancelToken?: CancelTokenSource,
  ): Promise<ManifestSearchResult> {
    const response = await this.http.post<ManifestSearchResult>(
      'search',
      criteria,
      {
        cancelToken: cancelToken?.token,
        params: { useSelectedDomainContext: true },
      },
    );
    return new ManifestSearchResult(response.data);
  }

  async previewManifest(
    criteria: ManifestCriteria,
    page: ManifestPreviewPage,
    cancelToken?: CancelTokenSource,
  ): Promise<ShipmentSearchResult> {
    const response = await this.http.post<ShipmentSearchResult>(
      'preview',
      {
        scac: criteria.scac,
        originLocationId: criteria.originLocationId,
        originLocationCode: criteria.originLocationCode,
        manifestDate: criteria.manifestDate.startOf('day').toISO(),
        ownerNaturalId: criteria.ownerNaturalId,
        page: page.page,
        pageSize: page.pageSize,
        sortField: page.sortField,
        sortDirection: page.sortDirection,
      },
      { cancelToken: cancelToken?.token },
    );
    return new ShipmentSearchResult(response.data);
  }

  async sendManifest(
    criteria: ManifestCriteria,
    excludedShipmentIds: number[],
  ): Promise<ManifestResponse> {
    const response = await this.http.post<ManifestResponse>('send', {
      scac: criteria.scac,
      originLocationId: criteria.originLocationId,
      originLocationCode: criteria.originLocationCode,
      manifestDate: criteria.manifestDate.startOf('day').toISO(),
      ownerNaturalId: criteria.ownerNaturalId,
      excludedShipmentIds: excludedShipmentIds,
    });
    return new ManifestResponse(response.data);
  }

  async cancelManifest(
    manifestId: number,
    cancelToken?: CancelTokenSource,
  ): Promise<void> {
    await this.http.delete<void>(`${manifestId}`, {
      cancelToken: cancelToken?.token,
    });
  }

  async getManifestShipments(
    manifestId: number,
    page: ManifestPreviewPage,
    cancelToken?: CancelTokenSource,
  ): Promise<ShipmentSearchResult> {
    const response = await this.http.get<ShipmentSearchResult>(
      `${manifestId}/shipments`,
      {
        params: {
          page: page.page,
          pageSize: page.pageSize,
          sortField: page.sortField,
          sortDirection: page.sortDirection,
        },
        cancelToken: cancelToken?.token,
      },
    );
    return new ShipmentSearchResult(response.data);
  }
}
